import { useEffect } from 'react';
import { getDeviceLabel, shouldGetDevice } from '../../../common/ambient';

// Helper to normalize input to always be an array
const normalizeDeviceIds = (deviceIds) => {
  if (!deviceIds) return [];
  return Array.isArray(deviceIds) ? deviceIds : [deviceIds];
};

// Helper to check if we need to fetch any devices
const shouldFetchAnyDevices = (device, deviceIds, stale) => {
  return deviceIds.some(id => shouldGetDevice(device, id, stale));
};


// Base hook that handles both single deviceId and arrays of deviceIds
const useDevice = (device, deviceActions, deviceIds, stale = 5) => {
  const deviceCache = device && device.deviceCache;
  const getDeviceFailures = device && device.getDeviceFailures;

  useEffect(() => {
    if (!device || !deviceActions || !deviceIds) {
      return;
    }

    // Normalize IDs inside the effect
    const normalizedIds = normalizeDeviceIds(deviceIds);
    if (normalizedIds.length === 0) {
      return;
    }

    // Check and fetch each device that needs updating
    normalizedIds.forEach(id => {
      if (shouldGetDevice(device, id, stale)) {
        deviceActions.getDevice(id);
      }
    });
  }, [device, deviceIds, deviceActions, stale]);

  // Normalize for the return value
  const normalizedIds = normalizeDeviceIds(deviceIds);

  // If we were passed a single ID (not an array), return a single device
  if (!Array.isArray(deviceIds)) {
    if (getDeviceFailures && getDeviceFailures.includes(deviceIds)) {
      return { _id: deviceIds, info: { name: 'Device Not Found' }, missing: true };
    }
    return (deviceCache && deviceCache[deviceIds]) || null;
  }

  // Otherwise, return an array of devices in the same order as the input IDs
  return normalizedIds.map(id => {
    if (getDeviceFailures && getDeviceFailures.includes(id)) {
      return { _id: id, info: { name: 'Device Not Found' }, missing: true };
    }
    return (deviceCache && deviceCache[id]) || null;
  });
};

// Export the base hook for direct use
export { useDevice };

// Maintain backward compatibility with existing exports
export { useDeviceData } from './deviceData';